import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { CoachingStatusCard } from "@/components/CoachingStatusCard";
import { InstallPrompt } from "@/components/InstallPrompt";
import { CoachAvatar } from "@/components/CoachAvatar";
import { toast } from "sonner";
import { Plus, Trash2, Target, MessageCircle, Flame, CheckCircle2, Briefcase, Heart, Leaf, Brain, Repeat, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CATEGORIES = [
  { key: "career", label: "Career", icon: Briefcase, color: "#E07A5F" },
  { key: "relationships", label: "Relationships", icon: Heart, color: "#C9846F" },
  { key: "wellness", label: "Wellness", icon: Leaf, color: "#81B29A" },
  { key: "mindset", label: "Mindset", icon: Brain, color: "#6B8E7F" },
  { key: "habits", label: "Habits", icon: Repeat, color: "#F2CC8F" },
  { key: "general", label: "General", icon: Sparkles, color: "#5C6A61" },
];

const catMeta = (k) => CATEGORIES.find((c) => c.key === k) || CATEGORIES[CATEGORIES.length - 1];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [goals, setGoals] = useState([]);
  const [usage, setUsage] = useState(null);
  const [newSessionCat, setNewSessionCat] = useState("general");
  const [newSessionOpen, setNewSessionOpen] = useState(false);
  const [newGoalOpen, setNewGoalOpen] = useState(false);
  const [goalTitle, setGoalTitle] = useState("");
  const [goalCat, setGoalCat] = useState("general");

  const loadAll = async () => {
    try {
      const [s, g, u] = await Promise.all([
        api.get("/sessions"),
        api.get("/goals"),
        api.get("/usage"),
      ]);
      setSessions(s.data);
      setGoals(g.data);
      setUsage(u.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const startSession = async () => {
    try {
      const { data } = await api.post("/sessions", { category: newSessionCat });
      setNewSessionOpen(false);
      navigate(`/chat/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start session");
    }
  };

  const deleteSession = async (id) => {
    try {
      await api.delete(`/sessions/${id}`);
      setSessions((s) => s.filter((x) => x.id !== id));
      toast.success("Session removed");
      loadAll();
    } catch {
      toast.error("Could not delete");
    }
  };

  const addGoal = async () => {
    if (!goalTitle.trim()) return;
    try {
      const { data } = await api.post("/goals", { title: goalTitle, category: goalCat });
      setGoals((gs) => [data, ...gs]);
      setGoalTitle("");
      setGoalCat("general");
      setNewGoalOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add goal");
    }
  };

  const updateGoal = async (id, patch) => {
    try {
      const { data } = await api.patch(`/goals/${id}`, patch);
      setGoals((gs) => gs.map((g) => (g.id === id ? data : g)));
    } catch {
      toast.error("Could not update");
    }
  };

  const removeGoal = async (id) => {
    try {
      await api.delete(`/goals/${id}`);
      setGoals((gs) => gs.filter((g) => g.id !== id));
    } catch {
      toast.error("Could not delete");
    }
  };

  const activeGoals = goals.filter((g) => !g.completed);
  const completedCount = goals.filter((g) => g.completed).length;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <AppHeader />

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-12">
        <InstallPrompt />
        {/* greeting */}
        <section className="flex items-center gap-6 mb-12">
          <CoachAvatar size="md" />
          <div>
            <div className="eyebrow text-[#5C6A61]">today with india</div>
            <h1 className="font-serif-display text-4xl md:text-5xl text-[#1C221D] mt-1">
              Welcome back, {user?.name?.split(" ")[0] || "friend"}.
            </h1>
            <p className="text-[#5C6A61] mt-2">
              What would you like to work on?{" "}
              <Link to="/words" data-testid="dashboard-words-link" className="text-[#E07A5F] underline underline-offset-4">
                Revisit your words to live by
              </Link>
            </p>
          </div>
        </section>

        {/* Bento grid */}
        <section className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {/* Primary CTA — start a session */}
          <div className="md:col-span-2 lg:col-span-2 bg-[#2A3B32] text-[#FDFBF7] rounded-3xl p-8 flex flex-col justify-between min-h-[220px]">
            <div>
              <div className="eyebrow text-[#F2CC8F]">start a conversation</div>
              <div className="font-serif-display text-3xl md:text-4xl mt-3 leading-tight">
                Say the thing you've been
                <br />carrying around.
              </div>
            </div>
            <Dialog open={newSessionOpen} onOpenChange={setNewSessionOpen}>
              <DialogTrigger asChild>
                <button
                  data-testid="start-session-btn"
                  className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#F2CC8F] text-[#2A3B32] font-medium w-fit hover:-translate-y-0.5 active:scale-95 transition-transform duration-200"
                >
                  <Plus size={18} /> New session
                </button>
              </DialogTrigger>
              <DialogContent data-testid="new-session-dialog" className="bg-white rounded-3xl border-[#2A3B32]/10">
                <DialogHeader>
                  <DialogTitle className="font-serif-display text-2xl">What are we exploring?</DialogTitle>
                </DialogHeader>
                <div className="mt-4">
                  <label className="eyebrow text-[#5C6A61] block mb-2">Life area</label>
                  <Select value={newSessionCat} onValueChange={setNewSessionCat}>
                    <SelectTrigger data-testid="session-category-select" className="w-full rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c.key} value={c.key} data-testid={`cat-opt-${c.key}`}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    onClick={startSession}
                    data-testid="confirm-start-session"
                    className="mt-6 w-full py-3 rounded-full bg-[#2A3B32] text-[#FDFBF7] hover:-translate-y-0.5 active:scale-95 transition-transform duration-200"
                  >
                    Begin
                  </button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Usage card */}
          <div data-testid="usage-card">
            <CoachingStatusCard status={usage} />
          </div>

          {/* Goals summary */}
          <div className="bg-[#F2EBE5] rounded-3xl p-6" data-testid="goals-summary-card">
            <div className="eyebrow text-[#5C6A61]">goals</div>
            <div className="mt-4 flex items-center gap-3">
              <Target size={20} className="text-[#2A3B32]" />
              <div>
                <div className="font-serif-display text-3xl text-[#1C221D]">{activeGoals.length}</div>
                <div className="text-xs text-[#5C6A61]">active · {completedCount} completed</div>
              </div>
            </div>
            <Dialog open={newGoalOpen} onOpenChange={setNewGoalOpen}>
              <DialogTrigger asChild>
                <button
                  data-testid="add-goal-btn"
                  className="mt-4 inline-flex items-center gap-2 text-sm text-[#2A3B32] underline underline-offset-4"
                >
                  <Plus size={14} /> add a goal
                </button>
              </DialogTrigger>
              <DialogContent data-testid="new-goal-dialog" className="bg-white rounded-3xl border-[#2A3B32]/10">
                <DialogHeader>
                  <DialogTitle className="font-serif-display text-2xl">A new goal</DialogTitle>
                </DialogHeader>
                <div className="mt-4 space-y-4">
                  <input
                    value={goalTitle}
                    onChange={(e) => setGoalTitle(e.target.value)}
                    placeholder="e.g. Have the hard conversation with Alex"
                    data-testid="goal-title-input"
                    className="w-full px-4 py-3 rounded-xl border border-[#2A3B32]/15 bg-[#FDFBF7] focus:outline-none focus:border-[#2A3B32]"
                  />
                  <Select value={goalCat} onValueChange={setGoalCat}>
                    <SelectTrigger data-testid="goal-category-select" className="w-full rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c.key} value={c.key} data-testid={`goal-cat-opt-${c.key}`}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    onClick={addGoal}
                    data-testid="confirm-add-goal"
                    className="w-full py-3 rounded-full bg-[#2A3B32] text-[#FDFBF7] hover:-translate-y-0.5 active:scale-95 transition-transform duration-200"
                  >
                    Add goal
                  </button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Recent sessions — large */}
          <div className="md:col-span-3 bg-white rounded-3xl p-6 md:p-8 border border-[#2A3B32]/10 shadow-[0_8px_30px_rgba(42,59,50,0.04)]">
            <div className="flex items-end justify-between mb-5">
              <div>
                <div className="eyebrow text-[#5C6A61]">your conversations</div>
                <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C221D] mt-1">
                  Recent sessions
                </h2>
              </div>
              <div className="text-sm text-[#5C6A61]" data-testid="sessions-usage">
                {sessions.length} conversation{sessions.length === 1 ? "" : "s"}
              </div>
            </div>
            {sessions.length === 0 ? (
              <div className="py-10 text-center text-[#5C6A61]">
                No conversations yet. Start one above when you're ready.
              </div>
            ) : (
              <ul className="divide-y divide-[#2A3B32]/8">
                {sessions.map((s) => {
                  const cat = catMeta(s.category);
                  return (
                    <li key={s.id} className="py-4 flex items-center gap-4 group" data-testid={`session-row-${s.id}`}>
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                        style={{ background: `${cat.color}22`, color: cat.color }}
                      >
                        <cat.icon size={18} />
                      </div>
                      <button
                        onClick={() => navigate(`/chat/${s.id}`)}
                        className="flex-1 min-w-0 text-left"
                        data-testid={`open-session-${s.id}`}
                      >
                        <div className="font-medium text-[#1C221D] truncate">{s.title}</div>
                        <div className="text-sm text-[#5C6A61] truncate">
                          {s.last_preview || "No messages yet"}
                        </div>
                      </button>
                      <div className="eyebrow text-[#5C6A61] hidden md:block">{cat.label}</div>
                      <button
                        onClick={() => deleteSession(s.id)}
                        className="text-[#5C6A61] hover:text-[#E07A5F] transition-colors opacity-60 group-hover:opacity-100"
                        data-testid={`delete-session-${s.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Active goals list */}
          <div className="md:col-span-3 lg:col-span-4 bg-white rounded-3xl p-6 md:p-8 border border-[#2A3B32]/10 shadow-[0_8px_30px_rgba(42,59,50,0.04)]">
            <div className="flex items-end justify-between mb-5">
              <div>
                <div className="eyebrow text-[#5C6A61]">insight into movement</div>
                <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C221D] mt-1">Your goals</h2>
              </div>
            </div>
            {goals.length === 0 ? (
              <div className="py-10 text-center text-[#5C6A61]">
                Nothing here yet. Add one small goal to begin.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {goals.map((g) => {
                  const cat = catMeta(g.category);
                  return (
                    <div
                      key={g.id}
                      className="p-5 rounded-2xl border border-[#2A3B32]/10 bg-[#FDFBF7] flex flex-col gap-3"
                      data-testid={`goal-card-${g.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center"
                            style={{ background: `${cat.color}22`, color: cat.color }}
                          >
                            <cat.icon size={16} />
                          </div>
                          <span className="eyebrow text-[#5C6A61]">{cat.label}</span>
                        </div>
                        <button
                          onClick={() => removeGoal(g.id)}
                          className="text-[#5C6A61] hover:text-[#E07A5F]"
                          data-testid={`delete-goal-${g.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <div className={`font-medium ${g.completed ? "line-through text-[#5C6A61]" : "text-[#1C221D]"}`}>
                        {g.title}
                      </div>
                      <div className="h-1.5 rounded-full bg-[#F2EBE5] overflow-hidden">
                        <div className="h-full bg-[#81B29A]" style={{ width: `${g.progress}%` }} />
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="10"
                          value={g.progress}
                          onChange={(e) => updateGoal(g.id, { progress: parseInt(e.target.value, 10) })}
                          data-testid={`goal-progress-${g.id}`}
                          className="flex-1 accent-[#2A3B32]"
                        />
                        <span className="text-sm text-[#5C6A61] w-10 text-right">{g.progress}%</span>
                      </div>
                      {g.completed && (
                        <div className="flex items-center gap-2 text-[#81B29A] text-sm">
                          <CheckCircle2 size={14} /> Complete
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
